import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import Notice from '@cartwhl/ui/molecules/Notice';
import UploadNotice from '@cartwhl/ui/molecules/UploadNotice';

import {
  AVATAR_PICKER_SUBPANEL_KEY,
  CUSTOM_TAB,
  GEN_SUBPANEL_KEY,
  UPLOAD_CHARACTER_HISTORY_TAB,
  UPLOAD_CHARACTER_SUBPANEL_KEY,
  UPLOAD_CHARACTER_TAB,
} from 'components/CharacterPanel/constants';
import {
  animationGenerationStore,
  characterPickerStore,
  navigationStore,
  noticesStore,
  uiStore,
} from 'stores';
import { PANEL_KEY_CHARACTER } from 'stores/uiStore';

function useVisualTop(padding = 16) {
  const [top, setTop] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;

    const update = () => setTop((vv?.offsetTop || 0) + padding);
    update(); // 1st paint

    vv?.addEventListener('resize', update); // fires on kbd open
    vv?.addEventListener('scroll', update); // panning / zoom
    window.addEventListener('orientationchange', update);

    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [padding]);

  return top;
}

export default function Notices() {
  const { notices } = useSnapshot(noticesStore.state);
  const { upload: characterUpload } = useSnapshot(characterPickerStore.state);
  const { jobData, promptRequest, videoUpload } = useSnapshot(
    animationGenerationStore.state,
  );
  const { openPanel, subPanelKey, subPanelSectionKey } = useSnapshot(
    uiStore.state,
  );
  const { modal } = useSnapshot(navigationStore.state);
  const top = useVisualTop(72);

  const isGenerationJob = characterPickerStore.isGenerationJob(
    characterUpload?.uploadID,
  );

  const genPanelOpen =
    openPanel === PANEL_KEY_CHARACTER && subPanelKey === GEN_SUBPANEL_KEY;

  // const avatarUploadPanelOpen =
  //   openPanel === PANEL_KEY_CHARACTER &&
  //   subPanelKey === AVATAR_PICKER_SUBPANEL_KEY &&
  //   subPanelSectionKey === CUSTOM_TAB;

  const uploadSubpanelOpen =
    openPanel === PANEL_KEY_CHARACTER &&
    subPanelKey === UPLOAD_CHARACTER_SUBPANEL_KEY &&
    (subPanelSectionKey === UPLOAD_CHARACTER_TAB ||
      subPanelSectionKey === UPLOAD_CHARACTER_HISTORY_TAB);

  const showCharacterUploadNotice =
    characterUpload &&
    !(
      (isGenerationJob && genPanelOpen) ||
      (!isGenerationJob && uploadSubpanelOpen)
    );

  const showVideoUploadNotice =
    videoUpload && videoUpload.status === 'ERROR' && openPanel !== 'history';

  // only show the prompt request notice if the user is looking at a different job
  const showPromptRequestNotice =
    promptRequest &&
    jobData &&
    jobData.jobID !== promptRequest.uploadID &&
    openPanel !== 'history';

  const subpanelKey = isGenerationJob
    ? characterUpload?.status === 'PROCESSING'
      ? GEN_SUBPANEL_KEY
      : AVATAR_PICKER_SUBPANEL_KEY
    : UPLOAD_CHARACTER_SUBPANEL_KEY;

  // const subpanelKey = isGenerationJob
  //   ? GEN_SUBPANEL_KEY
  //   : UPLOAD_CHARACTER_SUBPANEL_KEY;

  const subpanelSectionKey = isGenerationJob
    ? characterUpload?.status === 'PROCESSING'
      ? ''
      : CUSTOM_TAB
    : characterUpload?.status === 'SUCCESS'
      ? UPLOAD_CHARACTER_HISTORY_TAB
      : UPLOAD_CHARACTER_TAB;

  // const subpanelSectionKey = isGenerationJob
  //   ? ''
  //   : characterUpload?.status === 'SUCCESS'
  //     ? UPLOAD_CHARACTER_TAB
  //     : UPLOAD_CHARACTER_HISTORY_TAB;

  return (
    <div
      id="notice-holder"
      style={{ top: `calc(${top}px + env(safe-area-inset-top))` }}
      className={`pointer-events-none fixed left-0 z-100 ${
        openPanel === '' || modal.type !== '' ? 'w-full' : 'w-3/4'
      }`}
    >
      {showCharacterUploadNotice && (
        <UploadNotice
          errorText="Character processing failed"
          status={characterUpload.status}
          uploadPercent={characterUpload.fileTransferPercent}
          processingPercent={characterUpload.processingPercentage}
          handleClick={() => {
            uiStore.setOpenPanel('character');
            setTimeout(() => {
              uiStore.setSubPanelKey(subpanelKey);
              uiStore.setSubPanelSectionKey(subpanelSectionKey);
              if (characterUpload.status === 'SUCCESS') {
                characterPickerStore.removeUpload();
              }
            }, 100);
          }}
          handleClose={
            characterUpload.status === 'SUCCESS'
              ? characterPickerStore.removeUpload
              : undefined
          }
          type={isGenerationJob ? 'characterGeneration' : 'characterFile'}
        />
      )}

      {showVideoUploadNotice && (
        <UploadNotice
          errorText="Video processing failed"
          status={videoUpload.status}
          uploadPercent={0}
          processingPercent={0}
          handleClick={() => {
            uiStore.setOpenPanel('history');
            animationGenerationStore.removeUpload();
          }}
          handleClose={animationGenerationStore.removeUpload}
          type={'videoProcessing'}
        />
      )}

      {showPromptRequestNotice && (
        <UploadNotice
          errorText="Motion generation failed"
          status={promptRequest.status}
          uploadPercent={0}
          processingPercent={promptRequest.processingPercentage}
          handleClick={() => {
            uiStore.setOpenPanel('history');
          }}
          handleClose={() => {
            animationGenerationStore.removePromptRequest();
          }}
          type={'motionGeneration'}
        />
      )}

      {notices.map((notice) => (
        <Notice
          key={notice.id}
          removeCallback={() => noticesStore.remove(notice.id)}
          {...notice}
        />
      ))}
    </div>
  );
}
